// src/tabs/mes-taches/NotesPanel.jsx — « Mes tâches » LOT 3.
//
// Sous-onglet « Notes » : notes libres NON-cochables de Dimitri, rattachées
// à un scope (logement / proprio / entreprise / divers). Ex. « code portail
// = 1234 », « préférences proprio X ». Distinct des ménages et des tâches
// cochables (LOT 1).
//
// AUTONOME (self-loading) : le composant charge lui-même la table Supabase
// `admin_notes` (RLS admin-only) ET la config logements (fetchCfg) si elle
// n'est pas fournie en prop. Tous les props sont optionnels → il fonctionne
// monté n'importe où (le main-loop le branchera dans le sous-onglet Notes).
//
// Données dérivées :
//   - getAllLogsFlat(cfg) → résolution logement_id → NOM (jamais d'id brut).
//   - cfg.props          → résolution prop_id (proprio) → NOM + déduction
//                          automatique du prop_id depuis le logement choisi.
//
// Écritures : toujours { data, error } + log (jamais fire-and-forget).
// Body = TEXTE brut (pas de dangerouslySetInnerHTML — escaping React).

import { useState, useEffect, useCallback, useMemo } from "react"
import { StickyNote, Pencil, Trash2, Home, User, Building2, Pin } from "lucide-react"
import { DS } from "../../lib/ui"
import { supabase } from "../../supabase"
import { fetchCfg } from "../../lib/cfg-api"
import { getAllLogsFlat } from "../../lib/sousloc-helpers"

const SCOPES = [
  { value: "logement", label: "Logement" },
  { value: "proprio", label: "Proprio" },
  { value: "entreprise", label: "Entreprise" },
  { value: "divers", label: "Divers" },
]

const SCOPE_LABEL = SCOPES.reduce((acc, s) => ({ ...acc, [s.value]: s.label }), {})

// Icône de scope (présentation) — emojis remplacés par des icônes lucide.
const SCOPE_ICON = { logement: Home, proprio: User, entreprise: Building2, divers: Pin }
function ScopeIcon({ scope, size = 14 }) {
  const I = SCOPE_ICON[scope] || Pin
  return <I size={size} strokeWidth={2} />
}

const EMPTY_DRAFT = {
  id: null,
  title: "",
  body: "",
  scope: "divers",
  logement_id: "",
  prop_id: "",
}

// Déduit le prop_id (proprio) à partir du logement choisi via getAllLogsFlat.
// Le helper attache propId sur chaque log feuille/chambre.
function deducePropId(cfg, logementId) {
  if (!logementId) return ""
  const flat = getAllLogsFlat(cfg)
  return flat.find((l) => l.id === logementId)?.propId || ""
}

export default function NotesPanel({ cfg: cfgProp = null }) {
  const [cfg, setCfg] = useState(cfgProp)
  const [notes, setNotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [saving, setSaving] = useState(false)

  // Filtre d'affichage.
  const [filterScope, setFilterScope] = useState("all")

  // Brouillon d'édition (création si id=null, édition sinon).
  const [draft, setDraft] = useState(null)

  // ── Chargement cfg (si non fourni en prop) ────────────────────────────
  useEffect(() => {
    if (cfgProp) {
      setCfg(cfgProp)
      return
    }
    let alive = true
    fetchCfg()
      .then((c) => {
        if (alive) setCfg(c)
      })
      .catch((e) => {
        console.error("[notes] fetchCfg failed", e)
        if (alive) setCfg({ props: [] })
      })
    return () => {
      alive = false
    }
  }, [cfgProp])

  // ── Chargement des notes ──────────────────────────────────────────────
  const loadNotes = useCallback(async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from("admin_notes")
      .select("*")
      .order("scope", { ascending: true })
      .order("position", { ascending: true })
      .order("created_at", { ascending: true })
    if (error) {
      console.error("[notes] select admin_notes failed", error)
      setLoadError(error.message || "Lecture des notes échouée")
      setNotes([])
    } else {
      setLoadError(null)
      setNotes(data || [])
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    loadNotes()
  }, [loadNotes])

  // ── Résolveurs de NOMS (jamais d'id brut affiché) ─────────────────────
  const logName = useCallback(
    (logementId) => {
      if (!logementId) return null
      const flat = getAllLogsFlat(cfg)
      return flat.find((l) => l.id === logementId)?.nom || logementId
    },
    [cfg]
  )

  const propName = useCallback(
    (propId) => {
      if (!propId) return null
      return (cfg?.props || []).find((p) => p.id === propId)?.nom || propId
    },
    [cfg]
  )

  const allLogs = useMemo(() => getAllLogsFlat(cfg), [cfg])
  const allProps = useMemo(() => cfg?.props || [], [cfg])

  // ── Notes filtrées + regroupées par scope ─────────────────────────────
  const visibleNotes = useMemo(
    () => (filterScope === "all" ? notes : notes.filter((n) => n.scope === filterScope)),
    [notes, filterScope]
  )

  const grouped = useMemo(() => {
    const map = {}
    for (const n of visibleNotes) {
      ;(map[n.scope] = map[n.scope] || []).push(n)
    }
    return map
  }, [visibleNotes])

  // ── Actions ───────────────────────────────────────────────────────────
  const startCreate = () => setDraft({ ...EMPTY_DRAFT })
  const startEdit = (note) =>
    setDraft({
      id: note.id,
      title: note.title || "",
      body: note.body || "",
      scope: note.scope || "divers",
      logement_id: note.logement_id || "",
      prop_id: note.prop_id || "",
    })
  const cancelDraft = () => setDraft(null)

  // Mise à jour d'un champ du brouillon + déduction prop_id auto quand on
  // choisit un logement.
  const updateDraft = (patch) => {
    setDraft((d) => {
      const next = { ...d, ...patch }
      if ("logement_id" in patch && next.scope === "logement") {
        next.prop_id = deducePropId(cfg, patch.logement_id)
      }
      // Changement de scope : on nettoie les rattachements non pertinents.
      if ("scope" in patch) {
        if (patch.scope !== "logement") next.logement_id = ""
        if (patch.scope !== "logement" && patch.scope !== "proprio") next.prop_id = ""
        if (patch.scope === "logement" && next.logement_id) {
          next.prop_id = deducePropId(cfg, next.logement_id)
        }
      }
      return next
    })
  }

  const canSave = draft && draft.body.trim().length > 0 && !saving

  const handleSave = async () => {
    if (!draft || !draft.body.trim()) return
    setSaving(true)
    const payload = {
      title: draft.title.trim() || null,
      body: draft.body.trim(),
      scope: draft.scope,
      logement_id: draft.scope === "logement" ? draft.logement_id || null : null,
      prop_id:
        draft.scope === "logement" || draft.scope === "proprio"
          ? draft.prop_id || null
          : null,
    }

    let error
    if (draft.id) {
      const res = await supabase
        .from("admin_notes")
        .update(payload)
        .eq("id", draft.id)
        .select()
      error = res.error
    } else {
      const res = await supabase.from("admin_notes").insert(payload).select()
      error = res.error
    }

    if (error) {
      console.error("[notes] save admin_notes failed", error)
      alert("Enregistrement de la note échoué : " + (error.message || "erreur"))
      setSaving(false)
      return
    }
    setSaving(false)
    setDraft(null)
    await loadNotes()
  }

  const handleDelete = async (note) => {
    const label = note.title ? `« ${note.title} »` : "cette note"
    if (!window.confirm(`Supprimer ${label} ? Cette action est définitive.`)) return
    const { error } = await supabase.from("admin_notes").delete().eq("id", note.id)
    if (error) {
      console.error("[notes] delete admin_notes failed", error)
      alert("Suppression échouée : " + (error.message || "erreur"))
      return
    }
    await loadNotes()
  }

  // ── Rendu ─────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: DS.fontBody, color: DS.navy }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <h2 style={{ display: "flex", alignItems: "center", gap: 9, fontFamily: DS.fontDisplay, fontWeight: 600, color: DS.marineDark, margin: 0, fontSize: 22 }}>
          <StickyNote size={20} strokeWidth={2} /> Notes libres
        </h2>
        <button
          type="button"
          className="fbz-press fbz-tap"
          onClick={startCreate}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: DS.corail,
            color: "#fff",
            border: "none",
            borderRadius: DS.radiusControl,
            padding: "9px 15px",
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: DS.fontBody,
          }}
        >
          + Nouvelle note
        </button>
      </div>

      {/* Filtre par scope */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
        {[{ value: "all", label: "Tout" }, ...SCOPES].map((s) => {
          const active = filterScope === s.value
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => setFilterScope(s.value)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: active ? DS.marine : DS.card,
                color: active ? "#fff" : DS.textSecondary,
                border: `0.5px solid ${active ? DS.marine : DS.border}`,
                borderRadius: 999,
                padding: "5px 13px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: DS.fontBody,
              }}
            >
              {s.value !== "all" && <ScopeIcon scope={s.value} size={13} />}
              {s.label}
            </button>
          )
        })}
      </div>

      {/* Éditeur (création / édition) */}
      {draft && (
        <NoteEditor
          draft={draft}
          updateDraft={updateDraft}
          allLogs={allLogs}
          allProps={allProps}
          propName={propName}
          canSave={canSave}
          saving={saving}
          onSave={handleSave}
          onCancel={cancelDraft}
        />
      )}

      {/* États de chargement / erreur / vide */}
      {loading && <p style={{ color: DS.textLight }}>Chargement des notes…</p>}
      {loadError && !loading && (
        <p style={{ color: DS.red }}>Erreur de chargement : {loadError}</p>
      )}
      {!loading && !loadError && visibleNotes.length === 0 && (
        <p style={{ color: DS.textLight }}>
          Aucune note{filterScope !== "all" ? " dans ce filtre" : ""}. Cliquez sur « +
          Nouvelle note » pour en créer une.
        </p>
      )}

      {/* Notes regroupées par scope */}
      {!loading &&
        SCOPES.filter((s) => grouped[s.value]?.length).map((s) => (
          <section key={s.value} style={{ marginBottom: 22 }}>
            <h3
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                fontFamily: DS.fontBody,
                fontSize: 13,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                color: DS.textSecondary,
                margin: "0 0 8px",
              }}
            >
              <ScopeIcon scope={s.value} size={14} /> {SCOPE_LABEL[s.value]}
            </h3>
            <div style={{ display: "grid", gap: 10 }}>
              {grouped[s.value].map((note) => (
                <NoteCard
                  key={note.id}
                  note={note}
                  logName={logName}
                  propName={propName}
                  onEdit={() => startEdit(note)}
                  onDelete={() => handleDelete(note)}
                />
              ))}
            </div>
          </section>
        ))}
    </div>
  )
}

// ── Carte d'une note (lecture) ──────────────────────────────────────────
function NoteCard({ note, logName, propName, onEdit, onDelete }) {
  const ln = note.logement_id ? logName(note.logement_id) : null
  const pn = note.prop_id ? propName(note.prop_id) : null

  return (
    <div
      className="fbz-card"
      style={{
        background: DS.card,
        border: `0.5px solid ${DS.border}`,
        borderRadius: 12,
        padding: 14,
        boxShadow: "0 1px 3px rgba(33,83,135,.06)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          {note.title && (
            <div style={{ fontFamily: DS.fontDisplay, fontWeight: 600, color: DS.marineDark, marginBottom: 4 }}>
              {note.title}
            </div>
          )}
          {/* body = texte brut, escaping React (jamais dangerouslySetInnerHTML) */}
          <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", color: DS.marineDeep, lineHeight: 1.45 }}>
            {note.body}
          </div>
          {(ln || pn) && (
            <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
              {ln && (
                <span style={tagStyle(DS.cielSoft, DS.marine)}>
                  <Home size={12} strokeWidth={2} /> {ln}
                </span>
              )}
              {pn && (
                <span style={tagStyle(DS.corailSoft, DS.corailText)}>
                  <User size={12} strokeWidth={2} /> {pn}
                </span>
              )}
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
          <button
            type="button"
            className="fbz-press"
            onClick={onEdit}
            aria-label="Éditer la note"
            style={iconBtnStyle(DS.marine)}
          >
            <Pencil size={15} strokeWidth={2} />
          </button>
          <button
            type="button"
            className="fbz-press"
            onClick={onDelete}
            aria-label="Supprimer la note"
            style={iconBtnStyle(DS.danger)}
          >
            <Trash2 size={15} strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  )
}

function tagStyle(bg, fg) {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: bg,
    color: fg,
    borderRadius: 999,
    padding: "3px 11px",
    fontSize: 12,
    fontWeight: 600,
  }
}

function iconBtnStyle(color) {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: DS.card,
    border: `0.5px solid ${DS.border}`,
    borderRadius: 8,
    padding: "5px 8px",
    cursor: "pointer",
    color,
  }
}

// ── Éditeur de note (création / édition) ────────────────────────────────
function NoteEditor({
  draft,
  updateDraft,
  allLogs,
  allProps,
  propName,
  canSave,
  saving,
  onSave,
  onCancel,
}) {
  const labelStyle = {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: DS.textLight,
    margin: "0 0 4px",
  }
  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    border: `1px solid ${DS.border}`,
    borderRadius: 8,
    padding: "8px 10px",
    fontFamily: DS.fontBody,
    fontSize: 14,
    color: DS.navyDark,
  }

  return (
    <div
      style={{
        background: DS.card,
        border: `1px solid ${DS.ciel}`,
        borderRadius: 12,
        padding: 16,
        marginBottom: 18,
        display: "grid",
        gap: 12,
        boxShadow: "0 8px 24px rgba(33,83,135,.10)",
      }}
    >
      <div style={{ fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 16, color: DS.marineDark }}>
        {draft.id ? "Éditer la note" : "Nouvelle note"}
      </div>

      <div>
        <label style={labelStyle} htmlFor="note-title">
          Titre (optionnel)
        </label>
        <input
          id="note-title"
          style={inputStyle}
          value={draft.title}
          onChange={(e) => updateDraft({ title: e.target.value })}
          placeholder="Ex. Code portail"
        />
      </div>

      <div>
        <label style={labelStyle} htmlFor="note-body">
          Note *
        </label>
        <textarea
          id="note-body"
          style={{ ...inputStyle, minHeight: 80, resize: "vertical" }}
          value={draft.body}
          onChange={(e) => updateDraft({ body: e.target.value })}
          placeholder="Ex. Portail = 1234, clé sous le pot…"
        />
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 160px" }}>
          <label style={labelStyle} htmlFor="note-scope">
            Rattachement
          </label>
          <select
            id="note-scope"
            style={inputStyle}
            value={draft.scope}
            onChange={(e) => updateDraft({ scope: e.target.value })}
          >
            {SCOPES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {draft.scope === "logement" && (
          <div style={{ flex: "1 1 200px" }}>
            <label style={labelStyle} htmlFor="note-log">
              Logement
            </label>
            <select
              id="note-log"
              style={inputStyle}
              value={draft.logement_id}
              onChange={(e) => updateDraft({ logement_id: e.target.value })}
            >
              <option value="">— Choisir —</option>
              {allLogs.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.nom}
                </option>
              ))}
            </select>
            {draft.prop_id && (
              <div style={{ fontSize: 12, color: DS.textLight, marginTop: 4 }}>
                Proprio : {propName(draft.prop_id)}
              </div>
            )}
          </div>
        )}

        {draft.scope === "proprio" && (
          <div style={{ flex: "1 1 200px" }}>
            <label style={labelStyle} htmlFor="note-prop">
              Proprio
            </label>
            <select
              id="note-prop"
              style={inputStyle}
              value={draft.prop_id}
              onChange={(e) => updateDraft({ prop_id: e.target.value })}
            >
              <option value="">— Choisir —</option>
              {allProps.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nom}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button
          type="button"
          className="fbz-press"
          onClick={onCancel}
          style={{
            background: DS.card,
            color: DS.marine,
            border: `0.5px solid ${DS.borderHover}`,
            borderRadius: DS.radiusControl,
            padding: "9px 15px",
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: DS.fontBody,
          }}
        >
          Annuler
        </button>
        <button
          type="button"
          className="fbz-press"
          onClick={onSave}
          disabled={!canSave}
          style={{
            background: canSave ? DS.corail : DS.border,
            color: canSave ? "#fff" : DS.textMuted,
            border: "none",
            borderRadius: DS.radiusControl,
            padding: "9px 15px",
            fontWeight: 600,
            cursor: canSave ? "pointer" : "not-allowed",
            fontFamily: DS.fontBody,
          }}
        >
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>
    </div>
  )
}
